// Pure helpers for first-party vendor selection on the order form.
// Kept free of react-native imports so they can be unit-tested directly.

// Distinct first-party vendors (with a non-blank prefix) drawn from a product
// list, sorted by id. The first entry is the default selection.
export const firstPartyVendorsFromProducts = (products = []) => {
  const byId = new Map();

  products.forEach(product => {
    const vendor = product.vendor;

    if (vendor?.firstParty && vendor.prefix && !byId.has(vendor.id)) {
      byId.set(vendor.id, vendor);
    }
  });

  return [...byId.values()].sort((a, b) => (
    String(a.id).localeCompare(String(b.id), undefined, { numeric: true })
  ));
};

// Orders on a dealerProduct row, preferring those still visible to the
// dealer, sorted oldest-to-newest by createdAt so the last element is the
// most recent.
const relevantOrders = dealerProduct => {
  const all = dealerProduct.orders || [];
  const visible = all.filter(order => !order.hiddenFromDealerAt);
  const pool = visible.length ? visible : all;

  return [...pool].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  );
};

const mostRecentOrder = dealerProduct => {
  const orders = relevantOrders(dealerProduct);
  return orders.length ? orders[orders.length - 1] : null;
};

// Resolve the "Last Order" match for a product card, in priority order:
//   1. a direct order for this product,
//   2. a suggested-product link from another dealerProduct row (server-
//      authoritative; may be a same-vendor "Old Style" upgrade or a
//      cross-vendor bridge, e.g. ADR -> OklahomaTempTag).
// Returns the matching dealerProduct plus flags describing which case hit.
export const resolveLastOrderMatch = ({ product, dealer }) => {
  let match = dealer.dealerProducts.find(dealerProduct => (
    dealerProduct.product.id === product.id && mostRecentOrder(dealerProduct)
  ));
  let isSuggested = false;

  if (!match) {
    match = dealer.dealerProducts.find(dealerProduct => (
      dealerProduct.suggestedProduct?.id === product.id &&
      mostRecentOrder(dealerProduct)
    ));

    isSuggested = !!match;
  }

  const lastOrder = match ? mostRecentOrder(match) : null;
  const isCrossVendor = !!(
    isSuggested && match.product.vendor?.id !== product.vendor?.id
  );
  const matchedVendorName = isCrossVendor ? match.product.vendor.name : null;

  return { match, isSuggested, matchedVendorName, lastOrder };
};
